import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Link } from "../components/L";
import { Check, MapPin, ArrowRight, ShieldCheck, UserCheck, CalendarClock, Languages, ChevronRight } from "lucide-react";
import Seo from "../components/Seo";
import BookingForm from "../components/BookingForm";
import CTABand from "../components/CTABand";
import { getCity, cities, cityLabel, IMAGES } from "../mock";
import { landingsForCity, landingsGroup, getLandingData } from "../landingPages";
import { useLang } from "../context/LanguageContext";

const iconMap = { ShieldCheck, UserCheck, CalendarClock, Languages };

const CityPage = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const city = getCity(slug);
  if (!city) return <Navigate to="/staedte" replace />;

  const name = cityLabel(city, lang);
  const cpg = t.cityPage;
  const ce = t.cityExtra;
  const w = t.whyUs;
  const term = t.term;
  const quarters = city.quarters.join(", ");
  const pc = (t._pages || {})[`city:${city.slug}`] || {};
  const f = (k, d) => pc[k] || d;
  const cityLandings = landingsGroup(city.slug);

  const kw = lang === "de"
    ? `Putzfrau gesucht ${name}, Putzfrau ${name} gesucht, Putzfrau Vermittlung ${name}, Putzfrau ${name}, Haushaltshilfe ${name}, Putzhilfe ${name}, Putzfrau Vermittlung Schweiz, zuverlässige Putzfrau ${name} für Privathaushalt, geprüfte Putzfrau ${name} stundenweise, günstige Putzfrau ${name} Fixpreis, immer dieselbe Putzfrau ${name} buchen`
    : `cleaner ${name} wanted, looking for a cleaner ${name}, cleaner placement ${name}, cleaner ${name}, home help ${name}, house cleaner ${name}, cleaner placement Switzerland, reliable cleaner ${name} for private household, vetted cleaner ${name} hourly, affordable cleaner ${name} flat rate`;

  const metaTitle = lang === "de"
    ? `Putzfrau ${name} gesucht? Putzfrau-Service ${name} | MyCleaner.ch`
    : `Cleaner ${name} wanted? Cleaning service ${name} | MyCleaner.ch`;
  const metaDesc = lang === "de"
    ? `Putzfrau ${name} gesucht? MyCleaner.ch ist Ihr Putzfrau-Service in ${name}: geprüfte, erfahrene Putzfrau in ${name}, immer dieselbe Person, Fixpreis 43.90 CHF/Std., 1. Stunde gratis, ohne Kreditkarte.`
    : `Cleaner ${name} wanted? MyCleaner.ch is your cleaning service in ${name}: vetted, experienced cleaner in ${name}, always the same person, flat rate CHF 43.90/hr, 1st hour free, no credit card.`;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "LocalBusiness",
    name: `MyCleaner.ch – ${term} ${name}`, description: metaDesc, areaServed: name, priceRange: "CHF 43.90/hr",
  };

  return (
    <div>
      <Seo pageKey={`city:${city.slug}`} title={metaTitle} description={metaDesc} keywords={kw} jsonLd={jsonLd} />

      <section className="pt-[116px] pb-14 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm px-3 py-1.5 rounded-full mb-4">
              <MapPin className="w-4 h-4 text-teal-600" /> {cpg.badge(name, city.canton)}
            </div>
            <h1 className="font-head font-extrabold text-4xl md:text-5xl leading-[1.1] text-slate-900 mb-5">{f("heroTitle", cpg.title(name))}</h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">{f("heroSubtitle", cpg.subtitle(name))}</p>
            <Link to="/buchen" className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold text-[17px] px-7 py-4 rounded-xl hover:bg-teal-800 transition-colors shadow-lg shadow-teal-800/20">{cpg.bookBtn(name)} <ArrowRight className="w-5 h-5" /></Link>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img src={IMAGES.living} alt={lang === "de" ? `Sauberes Zuhause in ${name}` : `Clean home in ${name}`} className="w-full h-[360px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[900px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-3xl text-slate-900 mb-4">{f("introTitle", cpg.introTitle(name))}</h2>
          <p className="text-slate-600 text-lg leading-relaxed mb-4">{f("introP1", cpg.introP1(name))}</p>
          <p className="text-slate-600 text-lg leading-relaxed">{f("introP2", cpg.introP2(name, quarters))}</p>
        </div>
      </section>

      {/* SEO keyword block + quarters */}
      <section className="py-14 bg-[#faf6ee]">
        <div className="max-w-[900px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-semibold text-[#12211b] text-3xl mb-4">{f("seoHeading", ce.seoTitle(name))}</h2>
          <p className="text-slate-600 text-lg leading-relaxed mb-4">{f("seoP1", ce.seoP1(name))}</p>
          <p className="text-slate-600 text-lg leading-relaxed mb-8">{f("seoP2", ce.seoP2(name))}</p>
          <h3 className="font-head font-semibold text-[#12211b] text-2xl mb-2">{ce.quartersTitle(name)}</h3>
          <p className="text-slate-600 mb-5">{ce.quartersLead(name)}</p>
          <div className="flex flex-wrap gap-2.5">
            {city.quarters.map((q) => (
              <Link key={q} to="/buchen" className="inline-flex items-center gap-2 bg-white border border-[#ece4d3] rounded-full px-4 py-2 text-slate-700 text-sm hover:border-[#d9b45a] hover:text-teal-700 transition-colors">
                <MapPin className="w-3.5 h-3.5 text-[#b8893f]" /> {term} {name} – {q}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-amber-50/50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-10 items-start">
          <div className="flex flex-col gap-8">
            <div>
              <h2 className="font-head font-extrabold text-3xl text-slate-900 mb-5">{f("tasksTitle", cpg.tasksTitle(name))}</h2>
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                {t.tasks.map((tk) => (
                  <div key={tk.id} className="flex items-center gap-2.5 text-slate-700"><span className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span><span className="text-[15px]">{tk.label}</span></div>
                ))}
              </div>
            </div>

            <div className="space-y-5">
              {t.trustPoints.map((tp) => {
                const Icon = iconMap[tp.icon];
                return (
                  <div key={tp.title} className="flex items-start gap-3"><span className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0 border border-slate-100"><Icon className="w-4 h-4 text-teal-700" /></span><div><div className="font-semibold text-slate-900 text-sm">{tp.title}</div><div className="text-slate-500 text-[13px]">{tp.desc}</div></div></div>
                );
              })}
            </div>

            {/* Ihre Vorteile – balances column height with the form */}
            <div className="pt-8 border-t border-teal-100">
              <h3 className="font-head font-semibold text-slate-900 text-xl mb-6">{w.title}</h3>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-7">
                {w.items.map((it) => (
                  <div key={it.title} className="flex items-start gap-2.5">
                    <span className="w-6 h-6 rounded-md bg-teal-600 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">{it.title}</div>
                      <div className="text-slate-500 text-[13px] leading-snug">{it.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <Link to={`/ratgeber/putzfrau-${city.slug}-ratgeber`} className="inline-flex items-center gap-2 mt-6 text-teal-700 font-semibold hover:gap-3 transition-all">
                {lang === "de" ? `Ratgeber: Putzfrau ${name}` : `Guide: Cleaner ${name}`} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <BookingForm defaultCity={city.slug} title={`${term} ${name} – 1h ${lang === "de" ? "gratis" : "free"}`} />
        </div>
      </section>

      <section className="py-14 bg-amber-50/50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-2xl text-slate-900 mb-6">{cpg.nearbyTitle}</h2>
          <div className="flex flex-wrap gap-3">
            {cities.filter((c) => c.slug !== city.slug).map((c) => (
              <Link key={c.slug} to={`/putzfrau/${c.slug}`} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-5 py-2.5 text-slate-700 hover:border-teal-600 hover:text-teal-700 transition-colors">
                <MapPin className="w-4 h-4 text-teal-600" /> {term} {cityLabel(c, lang)} <ChevronRight className="w-4 h-4 text-slate-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {cityLandings.length > 0 && (
        <section className="py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-6">
            <h2 className="font-head font-extrabold text-2xl text-slate-900 mb-6">{lang === "de" ? `Beliebte Suchen in ${name}` : `Popular searches in ${name}`}</h2>
            <div className="flex flex-wrap gap-3">
              {cityLandings.map((l) => (
                <Link key={l.slug} to={`/${l.slug}`} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-5 py-2.5 text-slate-700 hover:border-teal-600 hover:text-teal-700 transition-colors">
                  <MapPin className="w-4 h-4 text-teal-600" /> {getLandingData(l.slug, lang).keyword.replace(/\?$/, "")} <ChevronRight className="w-4 h-4 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand city={name} />
    </div>
  );
};

export default CityPage;
